import {defaultCertificateCanvasDocument} from './certificateDefaults'
import {
  CertificateBlock,
  CertificateCanvasDocument,
  CertificateFieldKey,
  CertificateIssuance,
  CertificateRenderContext,
  CertificateRenderModel,
} from './certificateTypes'

type CertificateSnapshot = Record<string, string>

function readSnapshot(certificate: CertificateIssuance): CertificateSnapshot {
  try {
    return JSON.parse(certificate.certificateSnapshotJson) as CertificateSnapshot
  } catch {
    return {}
  }
}

function blockFromSnapshot(snapshot: CertificateSnapshot): CertificateBlock {
  return {
    title: 'Certificate',
    certificateHeading: snapshot.heading || 'CERTIFICATE OF COMPLETION',
    certificateIssuerName: snapshot.issuerName || 'AD ASTRA, INC.',
    certificateDepartment: snapshot.department || 'Learning and Development Department',
    certificateOrganizationName: snapshot.organizationName || 'AD ASTRA, INC.',
    certificateSignatoryName: snapshot.signatoryName || 'Elena Petrova-Toolsie',
    certificateSignatoryTitle: snapshot.signatoryTitle || 'President & CEO, Ad Astra, Inc.',
    certificateSecondSignatoryName: snapshot.secondSignatoryName || 'Brian Thornton',
    certificateSecondSignatoryTitle:
      snapshot.secondSignatoryTitle || 'Training & Quality, Ad Astra, Inc.',
    certificateSecondSignatureUrl: snapshot.secondSignatureUrl || '/media/logos/signature.png',
    certificateSubtitle:
      snapshot.subtitle || 'has successfully completed the in-person interpreter training program',
    certificateProgramDuration: snapshot.programDuration || '8 Hours (In-Person)',
    certificatePartnershipLine:
      snapshot.partnershipLine !== undefined
        ? snapshot.partnershipLine
        : 'conducted in partnership with the Maryland Department of Juvenile Services',
    certificateAccentColor: snapshot.accentColor || '#c1272d',
    certificateLogoUrl: snapshot.logoUrl || '/media/logos/Ad_Astra_Logo_Red_prod.png',
    certificateFooterText:
      snapshot.footerText ||
      'Ad Astra, Inc. · 8701 Georgia Avenue, Suite #808 · Silver Spring, MD 20910 · www.ad-astrainc.com',
    certificateCanvasJson: snapshot.certificateCanvasJson || '',
  }
}

function parseCanvas(raw: string | undefined): CertificateCanvasDocument | null {
  const trimmed = raw?.trim()
  if (!trimmed) return null
  try {
    const doc = JSON.parse(trimmed) as CertificateCanvasDocument
    return doc.version === 1 && Array.isArray(doc.layers) ? doc : null
  } catch {
    return null
  }
}

function ensureCanvas(block: CertificateBlock, trainingTitle: string): CertificateCanvasDocument {
  const doc =
    parseCanvas(block.certificateCanvasJson) ??
    defaultCertificateCanvasDocument(block, trainingTitle)
  const accent = block.certificateAccentColor || doc.accentColor || '#a3a242'
  return {
    ...doc,
    theme: doc.theme ?? 'classic',
    accentColor: accent,
    backgroundColor: doc.backgroundColor || '#fffdf8',
    layers:
      doc.layers.length > 0
        ? doc.layers
        : defaultCertificateCanvasDocument(block, trainingTitle).layers,
  }
}

function buildRenderContext(
  block: CertificateBlock,
  opts: {
    learnerName: string
    trainingTitle: string
    issuedDate?: string
    certificateId?: string
    trainingDuration?: string
  }
): CertificateRenderContext {
  const signatory = [block.certificateSignatoryName, block.certificateSignatoryTitle]
    .filter(Boolean)
    .join(' · ')

  const programDuration =
    opts.trainingDuration ||
    (block.certificateProgramDuration && block.certificateProgramDuration.trim() !== ''
      ? block.certificateProgramDuration
      : '8 Hours (In-Person)')

  return {
    learnerName: opts.learnerName || 'Jiang Zhenyu ( Alex)',
    trainingTitle:
      opts.trainingTitle || 'Interpreting in Educational Settings within DJS Youth Facilities',
    heading: block.certificateHeading || 'CERTIFICATE OF COMPLETION',
    subtitle:
      block.certificateSubtitle ||
      'has successfully completed the in-person interpreter training program',
    issuerName: block.certificateIssuerName || 'AD ASTRA, INC.',
    departmentName: block.certificateDepartment || 'Learning and Development Department',
    programDuration,
    partnershipLine:
      block.certificatePartnershipLine !== undefined
        ? block.certificatePartnershipLine
        : 'conducted in partnership with the Maryland Department of Juvenile Services',
    primarySignatoryName: block.certificateSignatoryName || 'Elena Petrova-Toolsie',
    primarySignatoryTitle: block.certificateSignatoryTitle || 'President & CEO, Ad Astra, Inc.',
    secondarySignatoryName: block.certificateSecondSignatoryName || 'Brian Thornton',
    secondarySignatoryTitle:
      block.certificateSecondSignatoryTitle || 'Training & Quality, Ad Astra, Inc.',
    footerText:
      block.certificateFooterText ||
      'Ad Astra, Inc. · 8701 Georgia Avenue, Suite #808 · Silver Spring, MD 20910 · www.ad-astrainc.com',
    signatoryLine: signatory || 'Elena Petrova-Toolsie · President & CEO, Ad Astra, Inc.',
    issuedDate: opts.issuedDate || 'July 10, 2026',
    certificateId: opts.certificateId || '',
  }
}

export function resolveCertificateField(
  key: CertificateFieldKey | string,
  ctx: CertificateRenderContext
): string {
  switch (key) {
    case 'learnerName':
      return ctx.learnerName
    case 'trainingTitle':
      return ctx.trainingTitle
    case 'heading':
      return ctx.heading
    case 'subtitle':
      return ctx.subtitle
    case 'issuerName':
      return ctx.issuerName
    case 'signatoryLine':
      return ctx.signatoryLine
    case 'issuedDate':
      return ctx.issuedDate
    case 'certificateId':
      return ctx.certificateId ? `Certificate ID ${ctx.certificateId}` : ''
    case 'programDuration':
      return ctx.programDuration || ''
    case 'partnershipLine':
      return ctx.partnershipLine || ''
    case 'departmentName':
      return ctx.departmentName || ''
    case 'primarySignatoryName':
      return ctx.primarySignatoryName || ''
    case 'primarySignatoryTitle':
      return ctx.primarySignatoryTitle || ''
    case 'secondarySignatoryName':
      return ctx.secondarySignatoryName || ''
    case 'secondarySignatoryTitle':
      return ctx.secondarySignatoryTitle || ''
    case 'footerText':
      return ctx.footerText || ''
    default:
      return ''
  }
}

function sanitizeFileName(value: string): string {
  return (
    value
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .slice(0, 80) || 'certificate'
  )
}

export function buildCertificateRenderModel(
  certificate: CertificateIssuance
): CertificateRenderModel {
  const values = readSnapshot(certificate)
  const block = blockFromSnapshot(values)
  const document =
    parseCanvas(values.certificateCanvasJson) ??
    ensureCanvas(block, values.trainingTitle || block.title)
  const context = buildRenderContext(block, {
    learnerName: values.learnerName || 'Certificate holder',
    trainingTitle: values.trainingTitle || 'Training',
    trainingDuration: values.programDuration,
    issuedDate: new Date(certificate.issuedAtUtc).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
    certificateId: certificate.certificateNumber,
  })
  const fileName = sanitizeFileName(
    [values.learnerName, values.trainingTitle].filter(Boolean).join(' - ') || 'certificate'
  )
  return {document, context, fileName}
}
